import React, { useEffect, useRef, useState } from "react";

import { setupTimer } from "app/timers";

import { ActionProps } from "../Action";
import ActionButton from "../ActionButton";
import ActionPopover from "../ActionPopover";

import MessageList from "./MessageList";
import Subscription from "./Subscription";
import { Blog, fetchBlog, markViewed, Post } from "./blog";

const REFRESH_INTERVAL = 1200000;

interface Props extends ActionProps {
    unreadCount: number;
    setUnreadCount: (count: number) => void;
}

export default function InboxAction(props: Props) {
    const {
        name,
        open,
        tooltip,
        iconId,
        onClick,
        onClose,
        fullScreen,
        showMenuButton,
        unreadCount,
        setUnreadCount
    } = props;

    // null = not loaded yet; MessageList renders its Loading state
    const [posts, setPosts] = useState<Post[] | null>(null);

    const retrieveBlog = () => {
        fetchBlog().then((blog: Blog) => {
            setUnreadCount(blog.unreadCount);
            setPosts(blog.posts);
        });
    };

    const openRef = useRef(open);

    useEffect(() => {
 openRef.current = open;
});

    // Do not refresh while open, may have unwanted effects
    const refreshBlog = () => {
        if (!openRef.current) {
            retrieveBlog();
        }
    };

    useEffect(() => setupTimer("inbox-refresh", refreshBlog, REFRESH_INTERVAL), []);

    const buttonElement = useRef<HTMLButtonElement>(null);

    const handleClick = () => onClick(name);
    const handleClose = () => onClose(name);

    const handleViewed = (post: Post) => {
        setUnreadCount(unreadCount - 1);

        if (posts) {
            setPosts([...posts]);
        }

        markViewed(post);
    };

    const markAllRead = () => {
        setUnreadCount(0);

        if (posts) {
            for (const post of posts) {
                if (!post.viewed) {
                    post.viewed = true;
                    markViewed(post);
                }
            }

            setPosts([...posts]);
        }

        onClose(name);
    };

    const button = showMenuButton ? (
        <ActionButton
            onClick={handleClick}
            tooltip={tooltip}
            iconId={iconId}
            active={open}
            badgeContent={unreadCount}
            buttonRef={buttonElement}
            trackingId={props.trackingId}
        />
    ) : null;

    return (
        <>
            {button}
            <ActionPopover
                fixedWidth={400}
                open={open}
                title={tooltip}
                onClose={handleClose}
                anchorEl={buttonElement.current}
                fullScreen={fullScreen}
            >
                <MessageList
                    posts={posts}
                    onViewed={handleViewed}
                    trackingId={`${props.trackingId}-messages`}
                />
                <Subscription
                    unreadCount={unreadCount}
                    onMarkAllRead={markAllRead}
                    trackingId={`${props.trackingId}-subscription`}
                />
            </ActionPopover>
        </>
    );
}
